import React, { useState, forwardRef } from "react";
import PropTypes from "prop-types";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { parseDateOnly } from "@/helpers/dateHelpers";
import { HugeiconsIcon } from "@hugeicons/react";
import { Calendar01Icon } from "@hugeicons/core-free-icons";

/**
 * DatePicker Component - Migrated from Material-UI to shadcn/ui
 * Maintains backward compatibility with Material-UI DatePicker props
 */
const DatePickerComponent = forwardRef((props, ref) => {
  const {
    value,
    name,
    placeholder = "__/__/____",
    disableFuture,
    disablePast,
    fullWidth,
    label,
    disabled,
    className,
    handleChange,
    format: dateFormat = "DD/MM/YYYY",
    helperText,
    minDate,
    maxDate,
    isSchedule,
    ...rest
  } = props;

  const [open, setOpen] = useState(false);
  
  // Timezone-safe conversion of the stored value to a Date for display.
  // `value` may be a Date (MUI-compat) or a "YYYY-MM-DD"/ISO string from the API.
  const dateValue = value instanceof Date ? value : parseDateOnly(value);
  
  // Map date-fns format from moment format
  const formatMap = {
    "DD/MM/YYYY": "dd/MM/yyyy",
    "MM/DD/YYYY": "MM/dd/yyyy",
    "YYYY-MM-DD": "yyyy-MM-dd",
  };
  const dateFnsFormat = formatMap[dateFormat] || "dd/MM/yyyy";

  const handleSelect = (selectedDate) => {
    if (selectedDate) {
      // Set time to 13:00:00 like the original
      const dateWithTime = new Date(selectedDate);
      dateWithTime.setHours(13, 0, 0, 0);
      handleChange({ name, value: dateWithTime });
    } else {
      handleChange({ name, value: null });
    }
    setOpen(false);
  };

  const displayValue = dateValue && !isNaN(dateValue.getTime())
    ? format(dateValue, dateFnsFormat)
    : "";

  // Calculate disabled dates
  const disabledMatcher = [];
  if (disableFuture) {
    disabledMatcher.push({ after: new Date() });
  }
  if (disablePast) {
    disabledMatcher.push({ before: new Date() });
  }
  if (minDate) {
    disabledMatcher.push({ before: new Date(minDate) });
  }
  if (maxDate) {
    disabledMatcher.push({ after: new Date(maxDate) });
  }

  return (
    <div className={cn("space-y-2", fullWidth && "w-full", className)}>
      {label && (
        <Label htmlFor={name}>
          {label}
        </Label>
      )}
      
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={name}
            ref={ref}
            variant="outline"
            disabled={disabled}
            className={cn(
              "w-full justify-start text-left font-normal",
              !displayValue && "text-muted-foreground",
              "date-picker-custom cursor-pointer"
            )}
            {...rest}
          >
            {isSchedule && (
              <span className="mr-2">
                <HugeiconsIcon icon={Calendar01Icon} size={16} />
              </span>
            )}
            
            {displayValue || placeholder}
            
            {!isSchedule && (
              <span className="ml-auto">
                <HugeiconsIcon icon={Calendar01Icon} size={16} className="date-picker-icon" />
              </span>
            )}
          </Button>
        </PopoverTrigger>
        
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={dateValue}
            onSelect={handleSelect}
            disabled={disabledMatcher.length > 0 ? disabledMatcher : disabled}
            initialFocus
            fromDate={minDate ? new Date(minDate) : undefined}
            toDate={maxDate ? new Date(maxDate) : undefined}
          />
        </PopoverContent>
      </Popover>
      
      {helperText && (
        <p className="text-sm text-muted-foreground">{helperText}</p>
      )}
    </div>
  );
});

DatePickerComponent.displayName = "DatePicker";

// Default props
DatePickerComponent.defaultProps = {
  name: "",
  value: null,
  label: "",
  fullWidth: false,
  disabled: false,
  className: "cursor-pointer",
  isSchedule: false,
  handleChange: () => {},
  format: "DD/MM/YYYY",
  helperText: "",
  placeholder: "__/__/____",
  disableFuture: false,
  disablePast: false,
  maxDate: undefined,
  minDate: undefined,
};

// Prop types
DatePickerComponent.propTypes = {
  name: PropTypes.any,
  value: PropTypes.any,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
  isSchedule: PropTypes.bool,
  handleChange: PropTypes.func.isRequired,
  helperText: PropTypes.string,
  placeholder: PropTypes.string,
  disableFuture: PropTypes.bool,
  disablePast: PropTypes.bool,
  format: PropTypes.string,
  minDate: PropTypes.any,
  maxDate: PropTypes.any,
};

// Export Component
export const DatePickers = DatePickerComponent;
